import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import {RoomEnvironment} from "three/examples/jsm/environments/RoomEnvironment";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import {ColladaLoader} from "three/examples/jsm/loaders/ColladaLoader";


enum ShineLevel {
    Low,
    Medium,
    High
}


/* tslint:disable variable-name */
interface IModelResult {
    scene: THREE.Group;
    animations: THREE.AnimationClip[];
    scenes: THREE.Group[];
    cameras: THREE.Camera[];
    asset: THREE.Object3D;
}
    const Types = {
        Mesh: 'Mesh'
    };
    const OverlayImageId: string = 'threed-preview-overlay';
    const BackgroundColor = 0xeeeeee;
    const PhotoSurfaceMaterial = 'PhotoSurfaceMaterial';
    const MIN_THREED_PREVIEW_SIZE = 512;
const MeshPhongMaterial = 'MeshPhongMaterial';
    interface IEventType {
        type: string;
    }
let shininessLevel: ShineLevel = ShineLevel.Low;



    export class CameraControl extends THREE.EventDispatcher {

        public autoRotate: boolean;
        public autoRotateSpeed: number;
        public minPolarAngle: number;
        public maxPolarAngle: number;
        public animationRequest: number;
        public minDistance: number;
        public maxDistance: number;
        public currentAutoRotateDegree: number;
        public orbitControls!: OrbitControls;

        private phiDelta: number;
        private thetaDelta: number;
        private scale: number;
        private rotationIncrementX: number;
        private rotationIncrementY: number;
        private scaleStart: number;
        private lastPosition: THREE.Vector3;
        private autoRotateTime: number = 0;
        private autoRotateStartTime: number;
        private userInteractionStarted: boolean;
        private readonly target: THREE.Vector3;
        private readonly noZoom: boolean;
        private readonly zoomSpeed: number;
        private readonly noRotate: boolean;
        private readonly rotateSpeed: number;
        private readonly dampingFactor: number;
        private readonly rotateStart: THREE.Vector2;
        private readonly EPS: number;


        public constructor(public camera: THREE.Camera, public domElement: HTMLElement) {
            super();
            this.target = new THREE.Vector3();
            this.noZoom = false;
            this.zoomSpeed = 0.8;
            this.minDistance = 0;
            this.maxDistance = Infinity;
            this.noRotate = false;
            this.rotateSpeed = 1.0;
            this.autoRotate = true;
            this.autoRotateSpeed = 2.0;
            this.minPolarAngle = 0;
            this.maxPolarAngle = Math.PI;
            this.dampingFactor = 0.1;
            this.phiDelta = 0;
            this.thetaDelta = 0;
            this.scale = 1;
            this.rotationIncrementX = 0;
            this.rotationIncrementY = 0;
            this.rotateStart = new THREE.Vector2();
            this.scaleStart = 1;
            this.EPS = 0.000001;
            this.lastPosition = new THREE.Vector3();
            this.autoRotateStartTime = 0;
            this.currentAutoRotateDegree = 0;
            this.userInteractionStarted = false;
            this.animationRequest = 0;
            domElement.addEventListener('contextmenu', e => {
                e.preventDefault();
            });
            this.initOrbit();
        }

        public update(photoLoaded: boolean = true, colladaLoaded: boolean = true): void {
            const offset = this.camera.position.clone();
            // noinspection JSSuspiciousNameCombination
            let theta = Math.atan2(offset.x, offset.z);

            // noinspection JSSuspiciousNameCombination
            let phi = Math.atan2(Math.sqrt(offset.x * offset.x + offset.z * offset.z), offset.y);
            if (this.autoRotate) {
                this.rotateLeft(this.getAutoRotationAngle(photoLoaded, colladaLoaded));
            }
            if (this.rotationIncrementX !== 0 || this.rotationIncrementY !== 0) {
                const calcDamp = Math.sqrt(1 - this.dampingFactor);
                this.rotateLeft(this.rotationIncrementX);
                this.rotateUp(this.rotationIncrementY);
                this.rotationIncrementX *= calcDamp;
                this.rotationIncrementY *= calcDamp;
            }
            theta += this.thetaDelta;
            phi += this.phiDelta;
            phi = CameraControl.clamp(this.minPolarAngle, phi, this.maxPolarAngle);
            phi = CameraControl.clamp(this.EPS, phi, Math.PI - this.EPS);
            const radius = CameraControl.clamp(this.minDistance, offset.length() * this.scale, this.maxDistance);
            offset.x = radius * Math.sin(phi) * Math.sin(theta);
            offset.y = radius * Math.cos(phi);
            offset.z = radius * Math.sin(phi) * Math.cos(theta);
            this.camera.position.copy(offset);
            this.camera.lookAt(this.target);
            this.thetaDelta = 0;
            this.phiDelta = 0;
            this.scale = 1;
            if (this.lastPosition.distanceTo(this.camera.position) > 0) {
                this.dispatchEvent(CameraControl.changeEvent as never);
                this.lastPosition.copy(this.camera.position);
            }
            this.updateControls();
        }

        public rotateLeft(angle: number): void {
            if (angle === undefined) {
                angle = this.getAutoRotationAngle();
            }
            if (this.autoRotate || this.userInteractionStarted) {
                this.thetaDelta -= angle;
            }
        }

        public updateControls():void {
            this.orbitControls.update();
        }

        private initOrbit():void {
            this.orbitControls = new OrbitControls(this.camera, this.domElement);
            this.orbitControls.enableDamping = true;

            // Remove the arrow overlay once the user interacts with the canvas.
            const hideOverlay = () => {
                this.removeOverlayImage();
                this.orbitControls.removeEventListener('start', boundFn);
            };
            const boundFn = hideOverlay.bind(this);
            this.orbitControls.addEventListener('start', boundFn);
        }

        private rotateUp(angle: number): void {
            if (angle === undefined) {
                angle = this.getAutoRotationAngle();
            }
            this.phiDelta -= angle;
        }

        private static clamp(low: number, value: number, high: number): number {
            return Math.max(low, Math.min(value, high));
        }

        private getAutoRotationAngle(photoLoaded: boolean = true, colladaLoaded: boolean = true): number {
            if (!this.autoRotateTime) {
                if (this.animationRequest && photoLoaded && colladaLoaded) {
                    this.autoRotateTime = new Date().getTime();
                    this.autoRotateStartTime = this.autoRotateTime;
                }
                return 0;
            }
            const now = new Date().getTime();
            const timeDelta = now - this.autoRotateTime;
            this.autoRotateTime = now;

            const degree = this.autoRotateSpeed * 2 * Math.PI / 1000;
            const autoRotate = degree * timeDelta;
            if (this.autoRotate && now - this.autoRotateStartTime > 1000) {
                if (this.currentAutoRotateDegree + degree * (timeDelta + 16) >= 0) {
                    this.autoRotate = false;
                    this.userInteractionStarted = true;
                    this.showOverlayImage();

                }
            }
            this.currentAutoRotateDegree += autoRotate;
            return autoRotate;
        }

        private removeOverlayImage(): void {
            const overlayImage = document.getElementById(OverlayImageId);
            if (overlayImage) {
                overlayImage.className = OverlayImageId + '-hidden';
                setTimeout(() => {
                    overlayImage.remove();
                }, 1000);
            }
        }
        private showOverlayImage(): void {
            const overlayImg = document.getElementById(OverlayImageId);
            if (overlayImg) {
                overlayImg.className = OverlayImageId + '-visible';
            }
        }

        private static changeEvent: IEventType = {type: 'change'};

    }


    export enum PreviewType {
        Photo,
        Backing
    }

    export class ProductPreviewer {

        private showFaceLines: boolean;
        private readonly useWebGL: boolean;
        private readonly autoRotate: boolean;
        private readonly useAmbientLight: boolean;
        private width: number;
        private height: number;
        private scene: THREE.Scene|null = null;
        private camera: THREE.PerspectiveCamera|null= null;
        private textureLoader: THREE.TextureLoader|null= null;
        private renderer: THREE.Renderer|null = null;
        private controls: CameraControl|null= null;
        private previousModel?: string|THREE.Object3D;
        private model: THREE.Object3D|null= null;
        private previousPhoto: any;
        private previousBacking: any;
        private photoTexture: THREE.Texture[] = [];
        private modelLength: number = 0;
        private light?: THREE.Object3D;
        private ambientLight?: THREE.Object3D;
        private photoBacking?: THREE.Texture;
        private photoLoaded: boolean;
        private modelsLoaded: boolean;
        private directLight?: THREE.Object3D;
        private gltfLoader: boolean = false;

        private readonly camZoomPower: number;
        private readonly modelSurfaceColor: any;
        private readonly showModelWithoutPhoto: boolean;
        private readonly acceptedImages: string[];
        private readonly acceptedModels: string[];

        constructor(private readonly container:HTMLElement, private readonly threeDPreviewColor: string = '', private readonly distanceFactor: number = 0.75) {
            this.camZoomPower = 3;
            this.useWebGL = true;
            this.autoRotate = true;
            this.useAmbientLight = false;
            this.modelSurfaceColor = null;
            this.showModelWithoutPhoto = true;
            this.showFaceLines = false;
            this.photoLoaded = false;
            this.modelsLoaded = false;
            this.acceptedImages = ['jpg','png','gif','webp'];
            this.acceptedModels = ['dae', 'glb', 'gltf'];
            this.width = container.offsetWidth;
            this.height = container.offsetHeight;
            this.initializeScene();
        }

        private initializeScene():void {
            this.scene = new THREE.Scene();
            // In the future the background could be a different color.  If you don't set it will be grey.
            this.scene.background =  new THREE.Color( 0xffffff );
            this.camera = new THREE.PerspectiveCamera(50, this.width / this.height, 0.1, 1000);
            this.initializeLights();
            this.textureLoader = new THREE.TextureLoader();
            this.initializeRenderer();
            this.initializeController();
            window.addEventListener('resize', this.onContainerResize.bind(this), false);
            if (this.renderer) {
                this.container.appendChild(this.renderer.domElement);
            } else {
                console.error('RendererImage is null');
            }
            requestAnimationFrame(this.animate.bind(this));
        }

        private initializeRenderer():void {
            this.renderer = new THREE.WebGLRenderer({antialias: true});
            (this.renderer as THREE.WebGLRenderer).setPixelRatio(window.devicePixelRatio);
            (this.renderer as THREE.WebGLRenderer).setClearColor(BackgroundColor, 1);
            this.renderer.setSize(this.width, this.height);

        }

        private initializeEnvironment():void {
             console.log('Environment');
             try {
                 // @types/three last version that works is 0.93 (even though we are using 0.122), PREMGenerator is not included in that
                 const pmremGenerator = new (THREE as any).PMREMGenerator(this.renderer);
                 pmremGenerator.compileEquirectangularShader();
                // Load a room environment
                 (this.scene as any).environment = pmremGenerator.fromScene(new RoomEnvironment()).texture;// TODO:

             } catch (error) {
                 console.log('Environment ', error);
             }
        }

        public initializeController():void {
            this.controls = new CameraControl(this.camera as THREE.Camera, this.container);
            this.controls.autoRotate = this.autoRotate;
            this.controls.autoRotateSpeed = 0.10;
            this.controls.minPolarAngle = 0.5;
            this.controls.maxPolarAngle = Math.PI - 0.5;
            this.controls.addEventListener('change', this.render.bind(this));
        }

        public loadModel(uriOrMesh:string|THREE.Object3D, images?: string[]):void {
            this.previousModel = uriOrMesh;
            this.appendOverlayImage();
            if (this.model) {
                if (this.scene) {
                    this.scene.remove(this.model);
                }
                this.model = null;
            }
            if (typeof uriOrMesh === 'string') {
                const extension = this.getExtension(this.acceptedModels, uriOrMesh);
                if (extension.length === 0) {
                    console.error('model type is not supported.');
                    return;
                }
                let loader:any;
                if (extension === 'gltf' || extension === 'glb') {
                    loader = new GLTFLoader();
                    this.gltfLoader = true;
                    // Re-initialize the lights for the GLFTFLoader...
                    this.initializeLights();
                } else {
                    loader = new ColladaLoader();
                }
                loader.setCrossOrigin('anonymous');
                loader.load(uriOrMesh, (res:IModelResult):void => {
                    this.model = res.scene;
                    this.initializeModel(this.model);
                    if (this.gltfLoader) {
                        this.initializeEnvironment();
                    }
                    this.modelsLoaded = true;
                    if (images && images.length > 0) {
                        this.loadPhotoFromBase64(images, PreviewType.Photo );
                    } else {
                        this.photoLoaded = true;
                    }

                });
            } else {
                this.model = uriOrMesh as THREE.Group;
                this.initializeModel(this.model);
                this.modelsLoaded = true;
                if (images && images.length > 0) {
                    this.loadPhotoFromBase64(images, PreviewType.Photo);
                } else {
                    this.photoLoaded = true;
                }

            }
        }

        private getExtension(extensions:string[], name:string):string {
            const ext = (name.split('.').pop() || '').toLowerCase();
            if (extensions.indexOf(ext) > -1) {
                return ext;
            }
            if (ext.indexOf('?') > -1) {
                const urlExt = (ext.split('?')[0].split('.')[0]).toLowerCase();
                if (extensions.indexOf(urlExt) > -1) {
                    return urlExt;
                }
            }
            return '';
        }

        private loadImageFromUri(uri:string, flag:PreviewType):void {
            if (uri.substring(0, 10) !== 'data:image' && this.getExtension(this.acceptedImages, uri).length === 0) {
                console.error('loadPhotoFromUri: extension not supported ' + uri);
                return;
            }
            if (this.textureLoader) {
                this.textureLoader.load(uri, texture => {
                    this.photoLoaded = true;
                    return this.initializeTexture(texture, flag);
                });
            } else {
                console.error('loadPhotoFromUri: textureLoader is not initialized');
            }
        }

        public loadPhotoFromUri(uri:string):void {
            this.previousPhoto = uri;
            this.loadImageFromUri(uri, PreviewType.Photo);
        }

        // noinspection JSUnusedGlobalSymbols
        public loadBackingFromUri(uri:string):void {
            this.previousBacking = uri;
            this.loadImageFromUri(uri, PreviewType.Photo);
        }

        // noinspection JSUnusedGlobalSymbols - Used in enterprise/customizer.ts
        public loadPhotoFromCanvas(canvas:HTMLCanvasElement, flag:PreviewType):void {
            const texture = new THREE.Texture(canvas, THREE.UVMapping);
            this.initializeTexture(texture, flag);
        }

        // noinspection JSUnusedGlobalSymbols
        /**
         *
         * @param imageSource[]  an array of either a base64 datauri, or a canvas
         * @param flag
         */
        public loadPhotoFromImageSources(imageSource:(HTMLCanvasElement|string)[], flag:PreviewType):void {
            for (let index = 0; index < imageSource.length; index ++) {
                if (typeof imageSource[index] === 'string') {
                    this.loadImageByIndex(imageSource[index] as string, index, flag);
                } else {
                    const texture = new THREE.Texture(imageSource[index] as HTMLCanvasElement, THREE.UVMapping);
                    this.initializeTexture(texture, flag, index);
                }
            }
        }

        public loadPhotoFromBase64(base64:string[], flag:PreviewType):void {
            for (let index = 0; index < base64.length; index ++) {
                this.loadImageByIndex(base64[index], index, flag);
            }
        }

        private loadImageByIndex(base64: string, index: number, flag: PreviewType):void {
            const image = document.createElement('img');
            const texture = new THREE.Texture(image, THREE.UVMapping);
            image.addEventListener('load', (): void => {
                this.photoLoaded = true;
                return this.initializeTexture(texture, flag, index);
            });
            image.src = (base64.substring(0, 10) !== 'data:image' ? 'data:image/png;base64,' : '') + base64;

        }

        public destroy():void {
            if (this.controls) {
                cancelAnimationFrame(this.controls.animationRequest);
            }
            this.scene = null;
            this.camera = null;
            this.controls = null;
            this.model = null;
            this.photoTexture = [];
            if (this.renderer && this.container.contains(this.renderer.domElement)) {
                this.container.removeChild(this.renderer.domElement);
            }
            const overlayImage = document.getElementById(OverlayImageId);
            if (overlayImage) {
                overlayImage.remove();
            }

        }

        public render():void {
            if (this.renderer && this.scene && this.camera) {
                this.renderer.render(this.scene, this.camera);
            }
        }

        public onContainerResize():void {
            this.width = this.container.offsetWidth;
            this.height = this.container.offsetHeight;
            if (this.camera) {
                this.updateCamera(false, this.modelLength, this.camera as THREE.PerspectiveCamera);
            } else {
                console.error('onContainerResize: camera is not initialized');
            }
            if (this.renderer) {
                this.renderer.setSize(this.width, this.height);
            } else {
                console.error('onContainerResize: renderer is not initialized');
            }
        }

        private animate():void {
            if (this.controls && this.camera) {
                this.controls.animationRequest = requestAnimationFrame(this.animate.bind(this));
                this.controls.update(this.photoLoaded, this.modelsLoaded);
                if (this.light) {
                    this.light.position.copy(new THREE.Vector3(this.camera.position.x, this.camera.position.y, this.camera.position.z));
                } else {
                    console.error('animate: light is not initialized');
                }
                this.render();
            } else {
                console.error('animate: controls or camera is not initialized');
            }
        }

        private initializeLights():void {
            if (this.scene) {
                if (this.light) {
                    this.scene.remove(this.light);
                }
                if (this.ambientLight) {
                    this.scene.remove(this.ambientLight);
                }
                if (this.directLight) {
                    this.scene.remove(this.directLight);
                }

                if (this.threeDPreviewColor.toLocaleLowerCase() == 'dark') {
                    this.light = this.useAmbientLight ?
                        new THREE.HemisphereLight(0xffffff, 0x404040, 3.76) :
                        new THREE.SpotLight(0xffffff, 2.2, 1000, 1, 0.7, 0.5);
                    this.scene.add(this.light);
                } else if (this.gltfLoader) {

                    this.light = new THREE.HemisphereLight();
                    this.scene.add(this.light);

                    this.directLight = new THREE.DirectionalLight(0xFFFFFF, 2.5);
                    this.directLight.position.set(0.5, 0, 0.866); // ~60º
                    this.scene.add(this.directLight);

                    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.94);
                    this.scene.add(this.ambientLight);
                }  else {
                    this.light = this.useAmbientLight ?
                        new THREE.HemisphereLight(0xffffff, 0x404040, 3.8) :
                        /*     new THREE.SpotLight(0xffffff, 1.2, 1000, 1, 0.7, 0.5);*/
                        new THREE.SpotLight(0xffffff, 1.65, 1000, 1, 0.7, 0.5);

                    let ambientLightIntensity;
                    let ambientLightColor;
                    if (shininessLevel === ShineLevel.Low) {
                        ambientLightIntensity = 4;
                        ambientLightColor = 0xb7b7b7;
                    } else {
                        ambientLightIntensity = 1.7;
                        ambientLightColor = 0x707070;
                    }

                    // we may be able to get rid of special lights like white_mug since we can get the shininess level from the material.
                    // We can also get other things like roughness if we want to have a different light settings
                    if (this.threeDPreviewColor.toLocaleLowerCase() === 'white_mug') {
                        ambientLightIntensity = 1.7;
                    }
                    this.ambientLight = new THREE.AmbientLight(ambientLightColor,  ambientLightIntensity);
                    this.scene.add(this.ambientLight);
                    this.scene.add(this.light);
                }
            } else {
                console.error('initializeLights: scene is not initialized');
            }
        }

        private initializeMaterials():void {
            if (!this.model) {
                return;
            }
            if (this.photoTexture) {
                for (let textureIndex = 0; textureIndex < this.photoTexture.length; textureIndex ++) {
                    this.replacePhotoSurfaceMaterial(this.model, this.getTextureNameByIndex(textureIndex));
                }
            }
            if (this.photoBacking) {
                this.model = this.replaceMaterials(this.model as THREE.Mesh, 'PhotoBackingMaterial');
            }
            if (this.modelSurfaceColor) {
                this.replaceColorSurfaceMaterial(this.model);
            }
        }

        private initializeTexture(texture:THREE.Texture, flag:PreviewType, index: number = 0):void {
            new Promise<void>(resolve => {
                texture.colorSpace = THREE.SRGBColorSpace;
                const image = texture.image as HTMLImageElement;
                // threejs is resizing the image to power of 2 and shrinks it to 256x256 if it's smaller than 512 which is too small resulting in a blurry image
                if (Math.min(image.width, image.height) < MIN_THREED_PREVIEW_SIZE) {
                    let newWidth;
                    let newHeight;
                    if (image.width > image.height) {
                        newWidth = MIN_THREED_PREVIEW_SIZE * (image.width / image.height);
                        newHeight = MIN_THREED_PREVIEW_SIZE;
                    } else {
                        newWidth = MIN_THREED_PREVIEW_SIZE;
                        newHeight = MIN_THREED_PREVIEW_SIZE * (image.height / image.width);
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(image, 0, 0, newWidth, newHeight);
                    } else {
                        console.error('initializeTexture: context is not initialized');
                    }
                    const newImage = document.createElement('img');
                    newImage.addEventListener('load', () => {
                        texture.image = newImage;
                        resolve();
                    });
                    newImage.src = canvas.toDataURL('jpg', 100);
                } else {
                    resolve();
                }
            }).then(() => {
                if (this.scene && this.model) {
                    this.scene.add(this.model);
                } else {
                    console.warn('initializeTexture: scene or model is not initialized');
                }
                if (flag === PreviewType.Photo) {
                    this.photoTexture[index] = texture;
                    this.photoTexture[index].needsUpdate = true;
                } else if (flag === PreviewType.Backing) {
                    this.photoBacking = texture;
                    this.photoBacking.needsUpdate = true;
                } else {
                    console.error('Invalid preview type: ' + flag);
                }
                if (this.model) {
                    this.initializeMaterials();
                }
            }, err => {
                console.error('initializeTexture: ' + err);
            });

        }

        private calcHypotenuse(adj:number, opp:number):number {
            return Math.sqrt(Math.pow(adj, 2) + Math.pow(opp, 2));
        }

        private toRadian(degree:number):number {
            return degree * (Math.PI / 180);
        }

        private parseLength(model:THREE.Object3D):number {
            const box = new THREE.Box3().setFromObject(model);
            const xLength = box.max.x - box.min.x;
            const yLength = box.max.y - box.min.y;
            const zLength = box.max.z - box.min.z;
            const diag = this.calcHypotenuse(xLength, yLength);
            return this.calcHypotenuse(diag, zLength);
        }

        private updateCamera(resetPosition:boolean, modelLength:number, camera:THREE.PerspectiveCamera):void {
            if (camera) {
                const minAspect = Math.min((this.width / this.height), (this.height / this.width));
                const minFOV = this.toRadian(camera.fov) * minAspect;
                const adjacent = (modelLength / 2) / Math.tan(minFOV / 2);
                const viewDist = adjacent + modelLength / 2;
                if (resetPosition) {
                    camera.position.set(0, 0, 0);
                    camera.position.z = viewDist * this.distanceFactor;
                    camera.position.y = this.autoRotate ? 0 : modelLength / this.distanceFactor;
                }
                const windowAspect = this.width / this.height;
                camera.aspect = windowAspect;
                let ymax;
                let ymin;
                let xmax;
                let xmin;
                if (windowAspect > 1.0) {
                    ymax = camera.near * Math.tan(this.toRadian(camera.fov) * 0.5);
                    ymin = -ymax;
                    xmin = ymin * windowAspect;
                    xmax = ymax * windowAspect;
                } else {
                    xmax = camera.near * Math.tan(this.toRadian(camera.fov) * 0.5);
                    xmin = -xmax;
                    ymin = xmin / windowAspect;
                    ymax = xmax / windowAspect;
                }
                camera.projectionMatrix.makePerspective(xmin, xmax, ymax, ymin, camera.near, camera.far);
            }
        }

        private initializeModel(model:THREE.Object3D):void {
            this.modelLength = this.parseLength(model);
            if (this.camera && this.controls) {
                this.updateCamera(true, this.modelLength, this.camera);
                this.controls.minDistance = this.modelLength / 2 + this.camera.near;
                this.controls.maxDistance = this.controls.minDistance * this.camZoomPower;
                this.controls.currentAutoRotateDegree = this.toRadian(-60);
                this.controls.rotateLeft(this.controls.currentAutoRotateDegree);
                this.initializeMaterials();
                if (this.photoTexture || this.showModelWithoutPhoto) {
                    if (this.scene) {
                        this.scene.add(model);
                    } else {
                        console.error('initializeModel: scene is not initialized');
                    }
                }
            } else {
                console.error('initializeModel: camera or controls is not initialized');
            }
        }

        private appendOverlayImage(): void {
            const overlayImage = document.createElement('div');
            overlayImage.id = OverlayImageId;
            this.container.append(overlayImage);

        }

        // public initializeFaceLines():void {
        //     if (this.scene) {
        //         if (this.faceLines) {
        //             this.scene.remove(this.faceLines);
        //         }
        //         if (this.showFaceLines) {
        //             this.faceLines = new THREE.FaceNormalsHelper(this.model as THREE.Object3D, 1, 0x00ff00, 1);
        //             this.scene.add(this.faceLines);
        //         }
        //     } else {
        //         console.error('initializeFaceLines: scene is not initialized');
        //     }
        // }

        // public reloadModelAndPhoto():void {
        //     if (this.previousModel) {
        //         this.loadModel(this.previousModel);
        //     }
        //     if (this.previousPhoto) {
        //         this.loadPhotoFromUri(this.previousPhoto);
        //     }
        // }

        private replaceMaterials(model:THREE.Object3D, option:string):THREE.Object3D {
            if (!(model instanceof THREE.Mesh)) {
                console.warn('replaceMaterials: cannot replace model when not THREE.Mesh: ' + (model as THREE.Object3D).uuid);
                return model;
            }
            if (Array.isArray(model.material)) {
                model.material = model.material.map((mat):THREE.Material|null => {
                    return this.replaceMatchedMaterial(mat as THREE.MeshBasicMaterial, option);
                }).filter(mat => mat !== null) as THREE.Material[];
            } else {
                const material = this.replaceMatchedMaterial(model.material as THREE.MeshBasicMaterial, option);
                if (material) {
                    model.material = material;
                }
            }
            return model;
        }

        private replaceMatchedMaterial(material:THREE.MeshBasicMaterial, flag?:string):THREE.Material|null {
            if (!this.photoTexture && !this.photoBacking) {
                console.warn('cannot replace material when material is not loaded');
                return null;
            }
            const texture = this.getTextureByName(flag || '');
            if (material.name === flag) {
                material.map = texture;
                material.needsUpdate = true;
            }
            return material;
        }

        private replacePhotoSurfaceMaterial(model:THREE.Object3D, name?:string):void {
            if (name === void 0) {
                name = PhotoSurfaceMaterial;
            }
            model.traverse((child: any):void => {
                if (child.type !== Types.Mesh) {
                    return;
                }

                if (Array.isArray(child.material)) {
                    child.material.forEach((mat:THREE.MeshBasicMaterial):void => {
                        if (mat.name === name) {
                            this.replaceMarkedMaterial(mat as THREE.MeshBasicMaterial, name);
                        }
                    });
                } else if (name && (child.name === name || child.material.name === name)) {
                    this.replaceMarkedMaterial(child.material as THREE.MeshBasicMaterial, name);
                }
            });
        }

        private replaceMarkedMaterial(material:THREE.MeshBasicMaterial, name: string):void {
            material.map = this.getTextureByName(name);
            // Potentially, in the future we may not want to do this unless the image is transparent.
            if (name === PhotoSurfaceMaterial) {
                material.transparent = true;
                material.color = material.color.convertLinearToSRGB();
                if (material.type === MeshPhongMaterial) {
                    const shininess = (material as THREE.MeshPhongMaterial).shininess;
                    if (shininess < 30) {
                        shininessLevel = ShineLevel.Low;
                    } else if (shininess < 60) {
                        shininessLevel = ShineLevel.Medium;
                    } else {
                        shininessLevel = ShineLevel.High;
                    }
                }
            }
            material.needsUpdate = true;
        }

        private replaceColorSurfaceMaterial(model:THREE.Object3D):void {
            model.traverse( (child:THREE.Object3D):void => {
                if (child instanceof THREE.Mesh && !Array.isArray(child.material) && child.material.name === 'ColorSurfaceMaterial') {
                    (child.material as THREE.MeshBasicMaterial).color = new THREE.Color(this.modelSurfaceColor);
                }
            });

        }

        private getTextureByName(name: string): THREE.Texture {
            if (name === PhotoSurfaceMaterial) {
                 return this.photoTexture[0];
            } else {
                return name.startsWith(PhotoSurfaceMaterial) ?
                    this.photoTexture[parseInt(name.replace(PhotoSurfaceMaterial, ''), 10)]
                    : this.photoBacking as THREE.Texture;
            }
        }

        private getTextureNameByIndex(index: number): string {
            return index > 0 ? PhotoSurfaceMaterial + index : PhotoSurfaceMaterial;
        }

    }


